'use client';

import { useRef, useState, type PointerEvent } from 'react';
import { type NodeProps } from '@xyflow/react';
import { NodeShell } from '../BaseNode';
import { usePipelineStore } from '@/lib/pipeline/store';

type Rect = { x: number; y: number; w: number; h: number };

export default function CropNode({ id, data }: NodeProps) {
  const update = usePipelineStore((s) => s.updateNodeData);
  const rt = usePipelineStore((s) => s.runtime[id]);

  // The upstream image becomes available after a run; we draw the crop box over it.
  const upstream = usePipelineStore((s) => {
    const e = s.edges.find((ed) => ed.target === id && ed.targetHandle === 'image');
    if (!e) return undefined;
    const r = s.runtime[e.source];
    return (r?.output as { image?: string } | undefined)?.image;
  });

  const d = data as { rect?: Rect };
  const rect = d.rect ?? { x: 0, y: 0, w: 1, h: 1 };
  const out = rt?.output as { image?: string } | undefined;

  const boxRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<Rect | null>(null);

  const normXY = (clientX: number, clientY: number) => {
    const b = boxRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (clientX - b.left) / b.width)),
      y: Math.min(1, Math.max(0, (clientY - b.top) / b.height)),
    };
  };

  // Drag to draw the crop box. Window listeners (not pointer-capture) make the drag robust even
  // if the cursor leaves the image; stopPropagation on pointerdown keeps React Flow from panning.
  const onDown = (e: PointerEvent) => {
    if (!upstream || !boxRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const start = normXY(e.clientX, e.clientY);
    setDraft({ x: start.x, y: start.y, w: 0, h: 0 });

    const move = (ev: globalThis.PointerEvent) => {
      const p = normXY(ev.clientX, ev.clientY);
      setDraft({
        x: Math.min(start.x, p.x),
        y: Math.min(start.y, p.y),
        w: Math.abs(p.x - start.x),
        h: Math.abs(p.y - start.y),
      });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setDraft((cur) => {
        if (cur && cur.w > 0.02 && cur.h > 0.02) update(id, { rect: cur });
        return null;
      });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const shown = draft ?? rect;

  return (
    <NodeShell id={id} type="crop">
      {upstream ? (
        <div
          ref={boxRef}
          className="relative nodrag nopan select-none touch-none rounded-lg overflow-hidden cursor-crosshair"
          onPointerDown={onDown}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={upstream}
            alt="source"
            className="w-full block pointer-events-none"
            draggable={false}
          />
          <div
            className="absolute border-2 border-[var(--accent)] bg-[var(--accent-soft)] pointer-events-none"
            style={{
              left: `${shown.x * 100}%`,
              top: `${shown.y * 100}%`,
              width: `${shown.w * 100}%`,
              height: `${shown.h * 100}%`,
            }}
          />
        </div>
      ) : (
        <div className="text-xs text-[var(--text-muted)] py-6 text-center border border-dashed border-[var(--border-subtle)] rounded-lg">
          Run once to load the image, then drag on it or use the sliders below
        </div>
      )}

      {/* Sliders — reliable crop control (also drag on the image above) */}
      {(
        [
          ['x', 'X'],
          ['y', 'Y'],
          ['w', 'W'],
          ['h', 'H'],
        ] as const
      ).map(([k, lbl]) => (
        <label key={k} className="text-[10px] text-[var(--text-muted)] block">
          <span className="flex justify-between">
            <span>{lbl}</span>
            <span className="text-[var(--text-secondary)]">{Math.round((rect[k] ?? 0) * 100)}%</span>
          </span>
          <input
            type="range"
            className="nodrag w-full accent-[var(--accent)]"
            min={0}
            max={1}
            step={0.01}
            value={rect[k] ?? (k === 'w' || k === 'h' ? 1 : 0)}
            onChange={(e) => update(id, { rect: { ...rect, [k]: Number(e.target.value) } })}
          />
        </label>
      ))}

      {out?.image && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={out.image} alt="cropped" className="rounded-lg w-full mt-1" />
      )}
    </NodeShell>
  );
}
